import { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Badge, Button, Chip, Field, Fold, List, Row, Section, fieldInputClass } from '@/components/ui'
import { Sheet, SheetDivider, SheetPad, SheetRow } from '@/components/Sheet'
import { toast } from '@/components/Toast'
import { useCanWrite } from '@/lib/access'
import type { GoalDetail, TaskFull } from '@/types'
import { t } from '@/lib/i18n'

// 步骤周期的选项。标签来自 models.task.cycles.*——与服务端 Task#cycle_label 同一份，
// 不在这里再抄一遍中文，否则改一处忘一处。
const CYCLES = ['once', 'daily', 'weekly'] as const
// 稿子里加步骤的分值是一排 chip + 一个「或者自己填一个」，取代小号数字框。
const POINTS = [1, 2, 3, 5, 10, 20, 50]

// 一次只可能开一个 sheet。用一个联合类型而不是七个布尔量：七个布尔量会允许
// 「编辑目标和确认删除同时开着」这种不存在的状态。
type GoalSheet = 'more' | 'add' | 'edit' | 'del' | 'import' | 'export' | 'step' | null

export default function Show({ goal, tasks }: { goal: GoalDetail; tasks: TaskFull[] }) {
  const canWrite = useCanWrite()
  const [sheet, setSheet] = useState<GoalSheet>(null)
  const close = () => setSheet(null)

  // ── 加步骤 ──
  const [title, setTitle] = useState('')
  const [points, setPoints] = useState('10')
  const [cycle, setCycle] = useState<string>('daily')

  // ── 编辑目标 ──
  const [eTitle, setETitle] = useState(goal.title)
  const [eWhy, setEWhy] = useState(goal.why ?? '')
  const [eDeadline, setEDeadline] = useState(goal.deadline ?? '')
  const [eTarget, setETarget] = useState(String(goal.target_points || ''))
  const [eStatus, setEStatus] = useState(goal.status)

  // ── 步骤操作（改名 / 改分值 / 改周期 / 删除）──
  const [editingTask, setEditingTask] = useState<TaskFull | null>(null)
  const [tTitle, setTTitle] = useState('')
  const [tPoints, setTPoints] = useState('')
  const [tCycle, setTCycle] = useState<string>('daily')

  // ── 导入 / 导出 ──
  const [copied, setCopied] = useState(false)
  const [importText, setImportText] = useState('')
  const [importErr, setImportErr] = useState('')

  // 周期词用服务端下发的 cycle_label，不在客户端再维护一张映射表。
  // 导入侧认中英两种写法（见 Task.cycle_from_label），所以导出结果始终能再导回去。
  const exportText = tasks.map((task) => `${task.title}||${task.cycle_label}||+${task.points}`).join('\n')

  // 打卡后留在本页：只回传会变的 props（步骤可用状态 / 目标进度 / 头部积分余额），
  // 保留滚动位置和本地 state（编辑表单、输入框、sheet）。
  //
  // toast 在这里也要接：ToastStack 不再放行服务端的 earn（见 Celebrate.tsx），
  // 不接的话从这一页打卡会完全没有反馈。
  const ONLY = ['goal', 'tasks', 'current_person', 'flash']

  const checkinTask = (task: TaskFull) => {
    router.post(`/checkin/${task.id}`, {}, {
      preserveScroll: true, preserveState: true, only: ONLY,
      onSuccess: () => toast(
        t('checkin.toast_done', { title: task.title, count: task.points }),
        { undo: () => undoTask(task) },
      ),
    })
  }

  const undoTask = (task: TaskFull) => {
    router.delete(`/checkin/${task.id}`, {
      preserveScroll: true, preserveState: true, only: ONLY,
      onSuccess: () => toast(t('checkin.toast_undone', { title: task.title })),
    })
  }

  const addTask = () => {
    const name = title.trim()
    router.post(`/goals/${goal.id}/tasks`, { title: name, points, cycle }, {
      onSuccess: () => {
        close()
        toast(t('goals.show.toast_step_added', { title: name }))
        setTitle(''); setPoints('10'); setCycle('daily')
      },
    })
  }

  const openStep = (task: TaskFull) => {
    setEditingTask(task)
    setTTitle(task.title)
    setTPoints(String(task.points))
    setTCycle(task.cycle)
    setSheet('step')
  }

  const saveStep = () => {
    router.patch(`/tasks/${editingTask!.id}`, { title: tTitle.trim(), points: tPoints, cycle: tCycle }, {
      onSuccess: () => { close(); toast(t('goals.show.toast_step_saved')) },
    })
  }

  const deleteStep = () => {
    const task = editingTask!
    close()
    router.delete(`/tasks/${task.id}`, { onSuccess: () => toast(t('goals.show.toast_step_deleted', { title: task.title })) })
  }

  const saveGoal = () => {
    router.patch(`/goals/${goal.id}`, {
      title: eTitle, why: eWhy, deadline: eDeadline, target_points: eTarget, status: eStatus,
    }, { onSuccess: () => { close(); toast(t('goals.show.toast_saved')) } })
  }

  const submitImport = () => {
    setImportErr('')
    router.post(`/goals/${goal.id}/tasks/import`, { text: importText }, {
      onError: (errors) => setImportErr((errors as Record<string, string>).import ?? t('goals.show.import_failed')),
      onSuccess: () => { close(); setImportText(''); toast(t('goals.show.toast_imported')) },
    })
  }

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportText)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // 剪贴板不可用（非安全上下文等）：告诉用户手动复制，而不是弹一个系统 alert
      // ——alert 会打断、样式不受控，而这只是一次「没复制上，自己选一下」。
      toast(t('goals.show.copy_failed'))
    }
  }

  // 「可做」与「本轮已完成」按 task.available 分，不是按 done_count。
  //
  // 稿子的目标页把步骤当一次性的（x.done 是个布尔），而真实模型有周期：一个「每日」
  // 步骤永远不会「完成掉」，它只是今天做过了。available 正是服务端算好的那个答案
  // （once 看 done_count，daily 看今天，weekly 看本周）。
  const todo = tasks.filter((x) => x.available)
  const rest = tasks.filter((x) => !x.available)
  const pct = goal.target_points > 0 ? goal.progress_pct : 0

  const stepMore = (task: TaskFull) => canWrite ? (
    <button
      type="button"
      onClick={() => openStep(task)}
      aria-label={t('goals.show.step_more_aria', { title: task.title })}
      aria-haspopup="dialog"
      className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-lg active:bg-sunk"
    >
      ⋯
    </button>
  ) : undefined

  return (
    <Layout
      bleed
      topbar={{
        back: '/goals',
        title: goal.title,
        right: (canWrite || goal.archived) ? (
          <button
            type="button"
            onClick={() => setSheet('more')}
            aria-label={t('goals.show.more')}
            aria-haspopup="dialog"
            className="min-w-tap min-h-tap flex items-center justify-center text-xl text-muted"
          >
            ⋯
          </button>
        ) : undefined,
      }}
    >
      {/* 稿子的 `.gcard`：目标信息头。原来这里是一段常驻在 DOM 里的五字段编辑表单，
          现在它进了 sheet —— 详情页第一屏该回答「进展如何」，不是「怎么改」。 */}
      <section className="mx-3 mt-3 p-[18px] bg-card rounded-card shadow-card">
        <h1 className="m-0 text-xl font-extrabold leading-[1.3]">{goal.title}</h1>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {goal.status === 'done'
            ? <Badge tone="green">{t('goals.index.status_done')}</Badge>
            : <Badge tone="brand">{t('goals.index.status_active')}</Badge>}
          {/* 归档徽章对所有权限层级都可见——与 Goals/Index.tsx 的行同一条规则，
              否则只读 / claim 权限在列表页看得到「已归档」，点进详情页反而看不到了。 */}
          {goal.archived && (
            <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-bold bg-sunk text-muted">
              {t('goals.index.status_archived')}
            </span>
          )}
          {goal.deadline && <span className="text-muted text-[13px]">{t('goals.show.due', { date: goal.deadline })}</span>}
        </div>
        {goal.why && <p className="m-0 mt-2 text-muted text-[13px]">{goal.why}</p>}

        <p className="m-0 mt-1.5 text-muted text-[13px]">
          {t('goals.show.meta', { steps: tasks.length, target: goal.target_points })}
        </p>

        {goal.target_points > 0 && (
          <>
            <div className="h-2.5 my-3.5 rounded-full bg-sunk overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand to-brand2 transition-[width] duration-500 ease-sheet"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted font-semibold">
              <span>{t('goals.show.progress_left', { done: tasks.length - todo.length, total: tasks.length })}</span>
              <span>{t('goals.index.progress', { earned: goal.earned, target: goal.target_points, pct })}</span>
            </div>
          </>
        )}

        {goal.prediction && (
          <div className="flex gap-2 items-start mt-3.5 px-3.5 py-[11px] rounded-ctl bg-tint text-brand text-[13px] font-semibold leading-[1.4]">
            <span aria-hidden="true">🔮</span>
            <span>{goal.prediction}</span>
          </div>
        )}
        {goal.target_points === 0 && (
          <p className="m-0 mt-2 text-muted text-[13px]">{t('goals.show.earned_no_target', { count: goal.earned })}</p>
        )}
      </section>

      {tasks.length === 0 ? (
        <Empty
          emoji="🪜"
          title={t('goals.show.empty_title')}
          action={canWrite ? <Button variant="ghost" onClick={() => setSheet('add')}>＋ {t('goals.show.add_step')}</Button> : undefined}
        >
          {canWrite ? t('goals.show.empty_writable') : t('goals.show.empty')}
        </Empty>
      ) : (
        <>
          {todo.length > 0 && (
            <Section title={t('goals.show.todo_steps', { count: todo.length })}>
              <List>
                {todo.map((task) => (
                  <Row
                    key={task.id}
                    lead={
                      <span className="inline-flex w-[26px] h-[26px] rounded-full items-center justify-center bg-sunk text-muted text-xs font-extrabold">
                        {tasks.indexOf(task) + 1}
                      </span>
                    }
                    title={task.title}
                    sub={task.cycle_label + (task.done_count > 0 ? ` · ${t('goals.show.done_times', { count: task.done_count })}` : '')}
                    pts={`+${task.points}`}
                    // 归档目标不给完成 —— 不是置灰而是整行不可点：置灰会让人以为「等等就能点」，
                    // 而归档的含义是这些步骤不再计分（服务端 Points.complete_task! 也会拒）。
                    onTap={canWrite && !goal.archived ? () => checkinTask(task) : undefined}
                    ariaLabel={t('goals.show.complete_aria', { title: task.title, count: task.points })}
                    right={stepMore(task)}
                  />
                ))}
              </List>
            </Section>
          )}

          {rest.length > 0 && (
            <Fold defaultOpen={todo.length === 0} summary={t('goals.show.done_steps', { count: rest.length })}>
              <List>
                {rest.map((task) => (
                  <Row
                    key={task.id}
                    done
                    lead={
                      <span className="inline-flex w-[26px] h-[26px] rounded-full items-center justify-center bg-green-tint text-green text-xs font-extrabold">
                        ✓
                      </span>
                    }
                    title={task.title}
                    sub={task.avail_label}
                    pts={`+${task.points}`}
                    right={
                      <span className="flex shrink-0 gap-1">
                        {canWrite && !goal.archived && (
                          <button
                            type="button"
                            onClick={() => undoTask(task)}
                            aria-label={t('checkin.undo_aria', { title: task.title })}
                            className="min-w-tap min-h-tap rounded-chip text-muted text-[13px] font-semibold active:bg-sunk active:text-red"
                          >
                            {t('checkin.undo')}
                          </button>
                        )}
                        {stepMore(task)}
                      </span>
                    }
                  />
                ))}
              </List>
            </Fold>
          )}
        </>
      )}

      {/* 稿子的 `.stickyAdd`：加步骤是这一页的主动作，不该藏在页面顶部要滑回去。
          z-[35] 在 tab bar（40）之下、内容之上 —— 见 S1 的 z 层表。 */}
      {canWrite && !goal.archived && tasks.length > 0 && (
        <>
          <div className="h-[74px]" />
          <div className="fixed left-3 right-3 z-[35] above-tabbar max-w-[536px] mx-auto">
            <Button onClick={() => setSheet('add')}>＋ {t('goals.show.add_step')}</Button>
          </div>
        </>
      )}

      {/* ── 加步骤 ── */}
      <Sheet open={sheet === 'add'} onClose={close} label={t('goals.show.add_step')} title={t('goals.show.add_step')}>
        <SheetPad>
          <Field label={t('goals.show.step_title')}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('goals.show.step_placeholder')}
              enterKeyHint="done"
              autoComplete="off"
              className={fieldInputClass}
            />
          </Field>
          <Field label={t('goals.show.points_label')}>
            <div className="flex gap-2 flex-wrap">
              {POINTS.map((p) => (
                <Chip key={p} pressed={points === String(p)} onClick={() => setPoints(String(p))}>{p}</Chip>
              ))}
            </div>
            <input
              type="number" inputMode="numeric" min={1} value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder={t('goals.show.points_custom')}
              className={fieldInputClass + ' mt-2'}
            />
          </Field>
          <Field label={t('goals.show.cycle_label')}>
            <div className="flex gap-2 flex-wrap">
              {CYCLES.map((c) => (
                <Chip key={c} pressed={cycle === c} onClick={() => setCycle(c)}>{t(`task.cycles.${c}`)}</Chip>
              ))}
            </div>
          </Field>
          <Button disabled={!title.trim() || !Number(points)} onClick={addTask}>{t('goals.show.save')}</Button>
        </SheetPad>
      </Sheet>

      {/* ── 目标操作（稿子的 #moreGoalSheet）── */}
      <Sheet open={sheet === 'more'} onClose={close} label={t('goals.show.more')} title={t('goals.show.more')}>
        {canWrite && <SheetRow icon="✏️" onClick={() => setSheet('edit')}>{t('goals.show.edit_goal')}</SheetRow>}
        <SheetRow icon="📤" onClick={() => setSheet('export')}>{t('goals.show.export_step')}</SheetRow>
        {canWrite && <SheetRow icon="📥" onClick={() => setSheet('import')}>{t('goals.show.import_step')}</SheetRow>}
        {canWrite && (
          <>
            <SheetDivider />
            {goal.archived ? (
              <SheetRow
                icon="↩"
                onClick={() => { close(); router.delete(`/goals/${goal.id}/archive`) }}
              >
                {t('goals.show.unarchive')}
              </SheetRow>
            ) : (
              <SheetRow
                icon="📦"
                tail={t('goals.show.archive_tail')}
                onClick={() => { close(); router.post(`/goals/${goal.id}/archive`) }}
              >
                {t('goals.show.archive')}
              </SheetRow>
            )}
            <SheetDivider />
            {/* 有打卡记录时不给删除按钮，只解释为什么——不让用户点了才被拒。
                服务端守卫仍在（GoalsController#destroy），它防的是绕过 UI 的请求。 */}
            {goal.deletable ? (
              <SheetRow icon="🗑️" tone="danger" tail={t('goals.show.delete_tail')} onClick={() => setSheet('del')}>
                {t('goals.show.delete')}
              </SheetRow>
            ) : (
              <p className="px-5 py-2 text-muted text-xs leading-[1.5]">{t('goals.show.delete_blocked')}</p>
            )}
          </>
        )}
        {goal.archived && <p className="px-5 py-2 text-muted text-xs leading-[1.5]">{t('goals.show.archived_hint')}</p>}
      </Sheet>

      {/* ── 确认删除（稿子的 #delSheet）：删除不给撤销，所以它前面是两层 ── */}
      <Sheet
        open={sheet === 'del'}
        onClose={close}
        label={t('goals.show.delete')}
        title={t('goals.show.delete_title', { title: goal.title })}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
          {t('goals.show.delete_warn', { count: tasks.length })}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={() => { close(); router.delete(`/goals/${goal.id}`) }}>
              {t('goals.show.delete_confirm_btn')}
            </Button>
            <Button variant="plain" onClick={close}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      {/* ── 编辑目标 ── */}
      <Sheet open={sheet === 'edit'} onClose={close} label={t('goals.show.edit_goal')} title={t('goals.show.edit_goal')}>
        <SheetPad>
          <Field label={t('goals.show.field_title')}>
            <input value={eTitle} onChange={(e) => setETitle(e.target.value)} className={fieldInputClass} />
          </Field>
          <Field label={t('goals.show.field_why')}>
            <input
              value={eWhy}
              onChange={(e) => setEWhy(e.target.value)}
              placeholder={t('goals.show.field_why_placeholder')}
              className={fieldInputClass}
            />
          </Field>
          <Field label={t('goals.show.field_deadline')}>
            <input type="date" value={eDeadline} onChange={(e) => setEDeadline(e.target.value)} className={fieldInputClass} />
          </Field>
          <Field label={t('goals.show.field_target')}>
            <input
              type="number" inputMode="numeric" min={0} value={eTarget}
              onChange={(e) => setETarget(e.target.value)}
              placeholder={t('goals.show.no_target')}
              className={fieldInputClass}
            />
          </Field>
          <Field label={t('goals.show.field_status')}>
            <div className="flex gap-2">
              <Chip pressed={eStatus === 'active'} onClick={() => setEStatus('active')}>{t('goals.index.status_active')}</Chip>
              <Chip pressed={eStatus === 'done'} onClick={() => setEStatus('done')}>{t('goals.index.status_done')}</Chip>
            </div>
          </Field>
          <Button disabled={!eTitle.trim()} onClick={saveGoal}>{t('goals.show.save')}</Button>
        </SheetPad>
      </Sheet>

      {/* ── 步骤操作：改名 / 改分值 / 改周期 / 删除 ──
          取代原来行尾那两个 16px 的 ✏️ 与 ✕（与内容相邻，规矩 §4 不允许）。 */}
      <Sheet open={sheet === 'step'} onClose={close} label={t('goals.show.step_actions')} title={editingTask?.title ?? ''}>
        <SheetPad>
          <Field label={t('goals.show.step_title')}>
            <input value={tTitle} onChange={(e) => setTTitle(e.target.value)} className={fieldInputClass} />
          </Field>
          <Field label={t('goals.show.points_label')}>
            <div className="flex gap-2 flex-wrap">
              {POINTS.map((p) => (
                <Chip key={p} pressed={tPoints === String(p)} onClick={() => setTPoints(String(p))}>{p}</Chip>
              ))}
            </div>
            <input
              type="number" inputMode="numeric" min={1} value={tPoints}
              onChange={(e) => setTPoints(e.target.value)}
              className={fieldInputClass + ' mt-2'}
            />
          </Field>
          <Field label={t('goals.show.cycle_label')}>
            <div className="flex gap-2 flex-wrap">
              {CYCLES.map((c) => (
                <Chip key={c} pressed={tCycle === c} onClick={() => setTCycle(c)}>{t(`task.cycles.${c}`)}</Chip>
              ))}
            </div>
          </Field>
          <div className="grid gap-2">
            <Button disabled={!tTitle.trim() || !Number(tPoints)} onClick={saveStep}>{t('goals.show.save')}</Button>
            <Button variant="plain" onClick={deleteStep}>🗑️ {t('goals.show.delete_step')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      {/* ── 导出 / 导入：内容与校验一行没改，只把外壳从居中 modal 换成 sheet ── */}
      <Sheet open={sheet === 'export'} onClose={close} label={t('goals.show.export_title')} title={t('goals.show.export_title')}>
        <SheetPad>
          <textarea
            readOnly
            value={exportText}
            rows={Math.min(10, Math.max(3, tasks.length))}
            className={fieldInputClass + ' font-mono text-[13px]'}
          />
          <div className="grid gap-2 mt-3">
            <Button onClick={copyExport}>{copied ? `${t('goals.show.copied')} ✓` : t('goals.show.copy')}</Button>
            <Button variant="plain" onClick={close}>{t('goals.show.close')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      <Sheet open={sheet === 'import'} onClose={close} label={t('goals.show.import_title')} title={t('goals.show.import_title')}>
        <SheetPad>
          <p className="m-0 mb-2 text-muted text-[13px]">
            {t('goals.show.import_hint_prefix')}
            <code>{t('goals.show.import_format')}</code>
            {t('goals.show.import_hint_suffix', { cycles: CYCLES.map((c) => t(`task.cycles.${c}`)).join(' / ') })}
          </p>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={8}
            placeholder={t('goals.show.import_placeholder')}
            className={fieldInputClass + ' font-mono text-[13px]'}
          />
          {importErr && <p className="m-0 mt-2 text-red-strong text-[13px]">{importErr}</p>}
          <div className="grid gap-2 mt-3">
            <Button disabled={!importText.trim()} onClick={submitImport}>{t('goals.show.import_confirm')}</Button>
            <Button variant="plain" onClick={close}>{t('goals.show.cancel')}</Button>
          </div>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
